import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TransactionAML,
  Response,
  TransactionStatusLog,
  TransactionWarning,
  BlacklistData,
  Document,
  DocumentStatus,
  DocumentType,
  Channel,
  BaseTransaction,
  ChangeLog,
  Pagination,
  BlacklistUserInfoData,
} from '@ui/src';
import {
  CustomerType,
  DocumentComment,
  ProblemTransactionCofProps,
} from '../../types';
import { customersComponentApi } from '../services/customers';

export interface ProblemOutgoingTransactionState {
  transactionIsLoading: boolean;
  transaction?: BaseTransaction;
  aml?: TransactionAML;
  transactionId?: string;
  requestInProgress?: boolean;
  customerType?: CustomerType;
  logs?: TransactionStatusLog[];
  blacklistDataVisible: boolean;
  blacklistItemVisible: boolean;
  warnings?: TransactionWarning[];
  blacklistData?: BlacklistData;
  blacklistItem?: BlacklistUserInfoData;
  panelIsOpen: boolean;
  chats?: Channel[];
  documents?: Document[];
  documentsPagination?: Pagination;
  document?: Document;
  senderCompanyOfficials?: ProblemTransactionCofProps[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  transactionComments?: DocumentComment[];
  transactionCommentsPagination?: Pagination;
}

const initialState: ProblemOutgoingTransactionState = {
  panelIsOpen: false,
  transactionIsLoading: true,
  transaction: undefined,
  aml: undefined,
  transactionId: undefined,
  logs: undefined,
  blacklistDataVisible: false,
  blacklistItemVisible: false,
};

export const problemOutgoingTransactionSlice = createSlice({
  name: 'problemOutgoingTransaction',
  initialState,
  reducers: {
    setPanelIsOpen: (state, action: PayloadAction<boolean>) => {
      state.panelIsOpen = action.payload;
    },
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.transactionId && action.payload.state) {
        state.transactionId = action.payload.transactionId;
      }
    },
    setBlacklistDataVisible: (state, action: PayloadAction<boolean>) => {
      state.blacklistDataVisible = action.payload;
    },
    setBlacklistData: (
      state,
      action: PayloadAction<BlacklistData | undefined>,
    ) => {
      state.blacklistData = action.payload;
    },
    setBlacklistItemVisible: (state, action: PayloadAction<boolean>) => {
      state.blacklistItemVisible = action.payload;
    },
    setBlacklistDataItem: (
      state,
      action: PayloadAction<BlacklistUserInfoData | undefined>,
    ) => {
      state.blacklistItem = action.payload;
    },
    clearTransactionState: (state) => {
      state.transaction = undefined;
      state.aml = undefined;
      state.transactionId = undefined;
      state.customerType = undefined;
      state.transactionIsLoading = true;
      state.logs = undefined;
      state.warnings = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.documents = undefined;
      state.documentsPagination = undefined;
      state.transactionComments = undefined;
      state.transactionCommentsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getTransactionStatementsChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder
      .addMatcher(
        customersComponentApi.endpoints.getTransactionAml.matchFulfilled,
        (state, action: PayloadAction<Response<TransactionAML>>) => {
          const data = action.payload.data.edges.map((item) => item.node);
          state.aml = data[0] || undefined;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerOutgoingTransactionWarnings
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionWarning>>) => {
          state.warnings = action.payload.data.edges.map((item) => item.node);
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerOutgoingTransactionWarning
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: TransactionWarning }> & {
            meta: {
              arg: {
                originalArgs: {
                  warningId: string;
                };
              };
            };
          },
        ) => {
          const nextWarnings = state.warnings?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningId) {
              result.blacklistDoc = action.payload.data.blacklistDoc;
              result.blacklistItem = action.payload.data.blacklistItem;
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomersTransactionLogs
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionStatusLog>>) => {
          state.logs = action.payload.data.edges.map((item) => item.node);
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerOutgoingTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          state.transaction = {
            ...action.payload.data,
          };
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getProblemTransactionChats
          .matchFulfilled,
        (state, action: PayloadAction<Response<Channel>>) => {
          if (state.panelIsOpen) {
            state.chats = action.payload.data.edges.map((item) => item.node);
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.createProblemTransactionChat
          .matchFulfilled,
        (state, action: PayloadAction<{ data: Channel }>) => {
          if (state.panelIsOpen) {
            const nextChats = ([] as Channel[]).concat(
              state.chats as Channel[],
            );

            nextChats.unshift(action.payload.data);

            state.chats = nextChats;
          }
        },
      );
    builder.addMatcher(
      customersComponentApi.endpoints.getProblemTransactionSenderClientsOfficias
        .matchFulfilled,
      (state, action: PayloadAction<Response<ProblemTransactionCofProps>>) => {
        state.senderCompanyOfficials = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getProblemTransactionsDocuments
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<Document>> & {
          meta: { arg: { originalArgs: { cursor?: string } } };
        },
      ) => {
        if (state.panelIsOpen) {
          let documents;

          if (!action?.meta?.arg?.originalArgs?.cursor) {
            documents = action.payload.data.edges?.map((item) => item.node);
          } else {
            documents = ([] as Document[]).concat(
              state.documents as Document[],
            );

            action.payload.data.edges?.forEach((item) =>
              documents.push(item.node),
            );
          }

          state.documents = documents;
          state.documentsPagination = action.payload.data.pageInfo;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.addDocumentToProblemTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        if (state.transaction) {
          const nextDocuments = ([] as Document[]).concat(
            state.documents as Document[],
          );

          nextDocuments.unshift(action.payload.data);

          state.documents = nextDocuments;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getProblemTransactionDocumentById
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        if (state.transaction) {
          const nextDocument = { ...action.payload.data };

          state.document = {
            ...nextDocument,
            commentsList: action.payload.data.comments?.edges.map(
              (item) => item.node,
            ),
          };
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.changeProblemTransactionDocumentStatus
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  status: DocumentStatus;
                };
              };
            };
          };
        },
      ) => {
        if (state.transaction) {
          const { ids, status } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };
            const currentDocumentId = ids.find((id) => result.id === id);

            if (result.id === currentDocumentId) {
              result.status = status;
            }

            return result;
          });

          if (state.document) {
            const nextDocument = { ...state.document };

            if (ids.find((item) => item === nextDocument.id)) {
              nextDocument.status = status;

              state.document = nextDocument;
            }
          }

          state.documents = nextDocuments;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.changeProblemTransactionDocumentCategory
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  category: DocumentType;
                };
              };
            };
          };
        },
      ) => {
        if (state.transaction) {
          const { ids, category } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };
            const currentDocumentId = ids.find((id) => result.id === id);

            if (result.id === currentDocumentId) {
              result.category = category;
            }

            return result;
          });

          if (state.document) {
            const nextDocument = { ...state.document };

            if (ids.find((item) => item === nextDocument.id)) {
              nextDocument.category = category;

              state.document = nextDocument;
            }
          }

          state.documents = nextDocuments;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteProblemTransactionDocuments
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                };
              };
            };
          };
        },
      ) => {
        if (state.transaction) {
          const { ids } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.filter((item) =>
            ids.every((id) => item.id !== id),
          );

          state.document = undefined;

          state.documents = nextDocuments;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.addCommentToProblemTransactionDocument
        .matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document && state.transaction) {
          const nextDocument = { ...state.document };
          let nextComments: DocumentComment[] = [];

          if (
            nextDocument.commentsList &&
            nextDocument.commentsList.length > 0
          ) {
            nextComments = ([] as DocumentComment[]).concat(
              nextDocument.commentsList,
            );
          }

          nextComments.push(action.payload.data);

          nextDocument.commentsList = nextComments;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.editCommentInProblemTransactionDocument
        .matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document && state.transaction) {
          const nextDocument = { ...state.document };
          nextDocument.commentsList = nextDocument.commentsList?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.document = nextDocument;
        }
      },
    );
    builder
      .addMatcher(
        customersComponentApi.endpoints
          .deleteCommentInProblemTransactionDocument.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          if (state.document && state.transaction) {
            const nextDocument = { ...state.document };

            nextDocument.commentsList = nextDocument?.commentsList?.filter(
              (item) => item.id !== action.payload.data.id,
            );

            state.document = nextDocument;
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getProblemTransactionComments
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<DocumentComment>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let comments;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            comments = action.payload.data.edges?.map((item) => item.node);
          } else {
            comments = ([] as DocumentComment[]).concat(
              state.transactionComments as DocumentComment[],
            );

            action.payload.data.edges?.forEach((item) =>
              comments.push(item.node),
            );
          }

          state.transactionComments = comments;
          state.transactionCommentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.addCommentToProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          let comments: DocumentComment[] = [];

          if (state.transactionComments) {
            comments = ([] as DocumentComment[]).concat(
              state.transactionComments,
            );
          }

          comments.unshift(action.payload.data);

          state.transactionComments = comments;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.editCommentInProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          state.transactionComments = state.transactionComments?.map((item) => {
            const result = { ...item };

            if (result.id === item.id) {
              result.text = action.payload.data.text;
            }

            return result;
          });
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.deleteCommentInProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          const nextTranscationComments = state?.transactionComments?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          state.transactionComments = nextTranscationComments;
        },
      );
  },
});

export const {
  clearTransactionState,
  setBlacklistData,
  setBlacklistDataVisible,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  changePanelState,
  setPanelIsOpen,
} = problemOutgoingTransactionSlice.actions;

export default problemOutgoingTransactionSlice.reducer;
